import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Button,
    Col,
    DatePicker,
    Row,
    Segmented,
    Space,
    Spin,
    Statistic,
    Table,
    Typography,
    message,
} from "antd";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis,
} from "recharts";
import axios from "axios";
import dayjs from "dayjs";
import Api from "../api";

const {RangePicker} = DatePicker;
const {Text} = Typography;

const DEFAULT_DAYS = 14;
const GOOGLE_FIT_CLIENT_ID = "209668934326-tq06ph5s7sgsaageonpfoq51roqdc7bp.apps.googleusercontent.com";
const GOOGLE_FIT_CLIENT_SECRET = "vGGYlfkuLeq1siyQeyZ6H6MI";
const GOOGLE_FIT_TIMEZONE = "Europe/Tallinn";
const HOUR_BUCKET_MS = 60 * 60 * 1000;

const normalizeToken = (value) => {
    if (value == null || value === "" || value === "null" || value === "undefined") {
        return null;
    }

    return String(value).trim();
};

const parseGoogleFitBuckets = (buckets, granularity) => {
    return (buckets || []).map((bucket) => {
        let steps = 0;
        const dataset = bucket.dataset?.[0];

        if (dataset?.point?.length) {
            dataset.point.forEach((point) => {
                steps += Number(point.value?.[0]?.intVal || 0);
            });
        }

        const midMillis = (+bucket.startTimeMillis + +bucket.endTimeMillis) / 2;
        const mid = dayjs(midMillis);

        return {
            label: granularity === "hour" ? mid.format("YYYY-MM-DD HH:mm") : mid.format("YYYY-MM-DD"),
            steps,
        };
    });
};

const requestGoogleFitSteps = (token, startDate, endDate, granularity) => {
    const body = {
        aggregateBy: [{
            dataTypeName: "com.google.step_count.delta",
        }],
        bucketByTime: granularity === "hour"
            ? {durationMillis: HOUR_BUCKET_MS}
            : {
                period: {
                    type: "day",
                    value: 1,
                    timeZoneId: GOOGLE_FIT_TIMEZONE,
                },
            },
        startTimeMillis: dayjs(startDate).startOf("day").valueOf(),
        endTimeMillis: dayjs(endDate).endOf("day").valueOf(),
    };

    return axios.post(
        "https://www.googleapis.com/fitness/v1/users/me/dataset:aggregate",
        body,
        {headers: {Authorization: `Bearer ${token}`}}
    );
};

const refreshGoogleFitAccessToken = (refreshToken) => axios.post("https://oauth2.googleapis.com/token", {
    client_id: GOOGLE_FIT_CLIENT_ID,
    client_secret: GOOGLE_FIT_CLIENT_SECRET,
    grant_type: "refresh_token",
    refresh_token: refreshToken,
});

const getGoogleFitErrorMessage = (err) => {
    const googleError = err?.response?.data?.error;

    if (typeof googleError === "string") {
        return googleError;
    }

    return googleError?.message
        || googleError?.status
        || err?.response?.data?.error_description
        || err.message
        || "Failed to load Google Fit steps";
};

const UserGoogleFitSteps = ({user}) => {
    const [loading, setLoading] = useState(false);
    const [loadingTracker, setLoadingTracker] = useState(true);
    const [granularity, setGranularity] = useState("day");
    const [range, setRange] = useState([
        dayjs().subtract(DEFAULT_DAYS - 1, "day"),
        dayjs(),
    ]);
    const [buckets, setBuckets] = useState([]);
    const [error, setError] = useState(null);
    const [tokenRefreshed, setTokenRefreshed] = useState(false);
    const [tracker, setTracker] = useState(user?.activity_tracker || null);

    const isGoogleFit = tracker?.type === "google-fit";
    const accessToken = normalizeToken(isGoogleFit ? tracker?.access_token : null);
    const refreshToken = normalizeToken(isGoogleFit ? tracker?.refresh_token : null);

    const reloadTracker = useCallback(async () => {
        if (!user?.user_id) {
            return null;
        }

        setLoadingTracker(true);

        try {
            const res = await Api.get(`/admin/users/${user.user_id}`);
            const nextTracker = res.data.data?.activity_tracker || null;
            setTracker(nextTracker);
            return nextTracker;
        } catch {
            return null;
        } finally {
            setLoadingTracker(false);
        }
    }, [user?.user_id]);

    const resolveAccessToken = useCallback(async (storedAccessToken, storedRefreshToken) => {
        if (storedRefreshToken) {
            try {
                const refreshResponse = await refreshGoogleFitAccessToken(storedRefreshToken);
                const refreshedToken = normalizeToken(refreshResponse.data?.access_token);

                if (refreshedToken) {
                    return {token: refreshedToken, refreshed: true};
                }
            } catch {
                if (!storedAccessToken) {
                    throw new Error("Could not refresh Google Fit access token.");
                }
            }
        }

        if (!storedAccessToken) {
            throw new Error("No Google Fit access token is available for this user.");
        }

        return {token: storedAccessToken, refreshed: false};
    }, []);

    const fetchSteps = useCallback(async (reloadFirst = false) => {
        const activeTracker = reloadFirst ? await reloadTracker() : tracker;
        const latestAccessToken = normalizeToken(activeTracker?.access_token);
        const latestRefreshToken = normalizeToken(activeTracker?.refresh_token);

        if (!latestAccessToken && !latestRefreshToken) {
            setError("No Google Fit access token is available for this user.");
            return;
        }

        const startDate = range[0].format("YYYY-MM-DD");
        const endDate = range[1].format("YYYY-MM-DD");

        setLoading(true);
        setError(null);
        setTokenRefreshed(false);

        try {
            let {token, refreshed} = await resolveAccessToken(latestAccessToken, latestRefreshToken);
            let response;

            try {
                response = await requestGoogleFitSteps(token, startDate, endDate, granularity);
            } catch (err) {
                const isAuthError = err.response?.status === 401
                    || String(getGoogleFitErrorMessage(err)).toLowerCase().includes("invalid authentication credentials");

                if (isAuthError && latestRefreshToken && !refreshed) {
                    const refreshResponse = await refreshGoogleFitAccessToken(latestRefreshToken);
                    token = normalizeToken(refreshResponse.data?.access_token);

                    if (!token) {
                        throw err;
                    }

                    refreshed = true;
                    response = await requestGoogleFitSteps(token, startDate, endDate, granularity);
                } else {
                    throw err;
                }
            }

            setTokenRefreshed(refreshed);
            setBuckets(parseGoogleFitBuckets(response.data.bucket, granularity));
        } catch (err) {
            const details = getGoogleFitErrorMessage(err);
            const isAuthError = err.response?.status === 401
                || String(details).toLowerCase().includes("invalid authentication credentials");

            setBuckets([]);

            if (isAuthError && !latestRefreshToken) {
                setError(
                    "Google Fit access token is expired or invalid, and this user has no refresh token stored. "
                    + "Ask the user to open the Fitlap app and reconnect Google Fit so a fresh token is saved."
                );
            } else if (isAuthError) {
                setError(
                    "Google Fit rejected the access token and refresh also failed. "
                    + "The user may need to reconnect Google Fit in the app."
                );
            } else {
                setError(details);
            }

            message.error("Google Fit request failed");
        } finally {
            setLoading(false);
        }
    }, [tracker, reloadTracker, resolveAccessToken, range, granularity]);

    useEffect(() => {
        reloadTracker();
    }, [user?.user_id]);

    useEffect(() => {
        if (!loadingTracker && isGoogleFit && (accessToken || refreshToken)) {
            fetchSteps(false);
        }
    }, [loadingTracker, isGoogleFit, accessToken, refreshToken, range, granularity]);

    const totalSteps = useMemo(
        () => buckets.reduce((sum, bucket) => sum + bucket.steps, 0),
        [buckets]
    );

    const tableData = useMemo(
        () => buckets.map((bucket, index) => ({
            key: `${bucket.label}-${index}`,
            ...bucket,
        })),
        [buckets]
    );

    const chartData = useMemo(
        () => buckets.map((bucket) => ({
            label: granularity === "hour"
                ? dayjs(bucket.label).format("HH:mm")
                : dayjs(bucket.label).format("DD.MM"),
            steps: bucket.steps,
        })),
        [buckets, granularity]
    );

    if (!user) {
        return <Spin/>;
    }

    if (!loadingTracker && !isGoogleFit) {
        return (
            <Alert
                type="info"
                showIcon
                message="This user does not have Google Fit connected."
            />
        );
    }

    if (!loadingTracker && !accessToken && !refreshToken) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Google Fit is connected but no usable tokens are stored."
            />
        );
    }

    return (
        <div className="google-fit-steps">
            <Row gutter={[24, 16]} align="top" justify="space-between" className="google-fit-steps__header">
                <Col xs={24} md={14} lg={12} xl={10}>
                    <Space direction="vertical" size={12} style={{width: "100%"}}>
                        <RangePicker
                            value={range}
                            allowClear={false}
                            style={{width: "100%", maxWidth: 320}}
                            onChange={(dates) => {
                                if (dates?.[0] && dates?.[1]) {
                                    setRange(dates);
                                }
                            }}
                        />
                        <Space wrap>
                            <Segmented
                                value={granularity}
                                options={[
                                    {label: "Daily", value: "day"},
                                    {label: "Hourly", value: "hour"},
                                ]}
                                onChange={setGranularity}
                            />
                            <Button type="primary" loading={loading || loadingTracker} onClick={() => fetchSteps(true)}>
                                Refresh
                            </Button>
                        </Space>
                    </Space>
                </Col>

                <Col xs={24} md={10} lg={12} xl={14} className="google-fit-steps__summary">
                    <Space direction="vertical" size={12} align="end" style={{width: "100%"}}>
                        <Text type="secondary" className="google-fit-steps__info">
                            Fetches step data directly from Google Fit using the user&apos;s existing tokens.
                            Nothing is saved or modified.
                        </Text>

                        {!loading && !loadingTracker && (
                            <Statistic title="Google Fit total" value={totalSteps}/>
                        )}

                        {tokenRefreshed && (
                            <Text type="secondary">Used a refreshed access token for this request only</Text>
                        )}
                    </Space>
                </Col>
            </Row>

            {!loadingTracker && !refreshToken && (
                <Alert
                    style={{marginTop: 16}}
                    type="warning"
                    showIcon
                    message="No refresh token stored"
                    description="If the stored access token has expired, Google Fit will reject the request until the user reconnects Google Fit in the app."
                />
            )}

            {error && (
                <Alert
                    style={{marginTop: 16}}
                    type="error"
                    showIcon
                    message="Google Fit request failed"
                    description={String(error)}
                />
            )}

            {(loading || loadingTracker) && buckets.length === 0 ? (
                <div style={{marginTop: 24}}><Spin/></div>
            ) : buckets.length > 0 && (
                <>
                    <div style={{marginTop: 16, height: 260}}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{top: 8, right: 8, left: 0, bottom: 0}}>
                                <CartesianGrid strokeDasharray="3 3"/>
                                <XAxis dataKey="label" tick={{fontSize: 10}} minTickGap={8}/>
                                <YAxis width={42} tick={{fontSize: 10}}/>
                                <ChartTooltip/>
                                <Bar dataKey="steps" name="Google Fit steps" fill="#19AEF0" radius={[4, 4, 0, 0]} maxBarSize={24}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <Table
                        style={{marginTop: 16}}
                        size="small"
                        loading={loading}
                        pagination={false}
                        scroll={{y: 420}}
                        dataSource={tableData}
                        columns={[
                            {
                                title: granularity === "hour" ? "Hour" : "Date",
                                dataIndex: "label",
                                width: 180,
                            },
                            {
                                title: "Google Fit steps",
                                dataIndex: "steps",
                                render: (value) => <Text strong>{value.toLocaleString()}</Text>,
                            },
                        ]}
                    />
                </>
            )}
        </div>
    );
};

export default UserGoogleFitSteps;
